import React, { Fragment, useState } from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import ListaAdmin from '../../Components/ListaAdmin';
import FilaAdmin, { Insignia } from '../../Components/FilaAdmin';
import PanelEdicion from '../../Components/PanelEdicion';
import SeccionPanel from '../../Components/SeccionPanel';

import useListaAdmin from '../../Hooks/useListaAdmin';
import useImagenesStorage from '../../Hooks/useImagenesStorage';

const VACIO = { nombre: '', certificado: '', imagen: '' };
const CAMPOS_BUSQUEDA = ['nombre', 'certificado'];

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

const iniciales = nombre => String(nombre ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map(palabra => palabra[0].toUpperCase()).join('');

// Profesores de cursos y autores de blogs: nombre, certificado y foto; cambia el helper, los textos y la carpeta de Storage.
// carpeta: dónde se suben las fotos nuevas; carpetasPropias: carpetas (codificadas como en la URL) cuyas fotos se pueden borrar
const VistaPersonas = ({ helper, seccion, encabezado, textos, usadoEn, carpeta, carpetasPropias, prefijo }) => {
    const { recargar } = useAuth();
    const [guardando, setGuardando] = useState(false);
    const [foto, setFoto] = useState(null);

    const storage = useImagenesStorage();
    const l = useListaAdmin({ obtener: helper.obtener, obtenerUso: helper.obtenerUso, vacio: VACIO, camposBusqueda: CAMPOS_BUSQUEDA });

    const cuantos = n => n + ' ' + (n === 1 ? usadoEn.singular : usadoEn.plural);
    const enUso = l.editar ? l.uso[l.editar._id] ?? 0 : 0;
    const agregando = l.panel.abierto === 'agregar';
    const esFotoPropia = url => Boolean(url) && carpetasPropias.some(ruta => url.includes('/o/' + ruta));

    // El data URL solo es para la miniatura; a la API va la URL de Storage
    const elegirFoto = e => {
        const archivo = e.target.files[0];
        if (!archivo) return;
        setFoto(archivo);
        const lector = new FileReader();
        lector.addEventListener('load', () => l.cambiar({ imagen: lector.result }));
        lector.readAsDataURL(archivo);
    }

    const cerrar = async () => {
        if (await l.panel.cerrar()) setFoto(null);
    }

    const errores = () => {
        const faltantes = [
            [l.borrador.nombre.trim(), 'Nombre'],
            [l.borrador.certificado.trim(), textos.certificado],
            [agregando ? foto : l.borrador.imagen, 'Foto']
        ].filter(([valor]) => !valor).map(([, nombre]) => nombre);
        return faltantes.length ? 'Faltan datos por llenar: ' + faltantes.join(', ') : null;
    }

    // Orden: subir la foto nueva → guardar en la API → solo si se guardó, borrar la anterior.
    // Si la API falla, se borra la recién subida para no dejarla huérfana
    const guardar = async e => {
        e.preventDefault();
        const error = errores();
        if (error) return Swal.fire('Error!', error, 'error');
        setGuardando(true);

        let imagen = agregando ? '' : l.editar.imagen;
        if (foto) {
            imagen = await storage.subir(foto, carpeta, prefijo + '_' + Date.now());
            if (!imagen) {
                setGuardando(false);
                return Swal.fire('Error!', 'No se pudo subir la foto', 'error');
            }
        }

        const datos = { nombre: l.borrador.nombre.trim(), certificado: l.borrador.certificado.trim(), imagen };
        const res = agregando ? await helper.agregar(datos) : await helper.editar(datos, l.editar._id);
        if (!res?.success) {
            if (foto) await storage.eliminar(imagen);
            return setGuardando(false);
        }
        if (foto && !agregando && esFotoPropia(l.editar.imagen)) await storage.eliminar(l.editar.imagen);
        recargar();
    }

    const eliminar = async () => {
        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            titleText: '¿Eliminar a ' + l.editar.nombre + '?',
            text: 'También se elimina su foto.',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!isConfirmed) return;
        setGuardando(true);
        const res = await helper.eliminar(l.editar._id);
        if (!res?.success) return setGuardando(false);
        if (esFotoPropia(l.editar.imagen)) await storage.eliminar(l.editar.imagen);
        recargar();
    }

    const abrirAgregar = async () => {
        if (await l.abrirAgregar()) setFoto(null);
    }

    const abrirEditar = async registro => {
        if (await l.abrirEditar(registro)) setFoto(null);
    }

    return (
        <Fragment>
            <SideNav />
            {guardando && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={Boolean(l.panel.abierto)}
                    titulo={agregando ? textos.agregar : textos.editar}
                    ancho={l.panel.ancho}
                    onArrastre={l.panel.iniciarArrastre}
                    onCerrar={cerrar}
                    hayCambios={l.hayCambios}
                    pie={
                        <Fragment>
                            {!agregando && (
                                <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar} disabled={enUso > 0}>
                                    Eliminar
                                </button>
                            )}
                            <button type='submit' form='form_persona' className='boton_admin' disabled={!l.hayCambios}>
                                {agregando ? textos.agregar : 'Guardar cambios'}
                            </button>
                        </Fragment>
                    }
                >
                    {l.panel.abierto && (
                        <form id='form_persona' key={l.version} onSubmit={guardar}>
                            <SeccionPanel id='datos' numero={1} titulo='Datos' ubicacion={textos.ubicacion}>
                                <div className="input-field">
                                    <input onChange={e => l.cambiar({ nombre: e.target.value })} value={l.borrador.nombre} id='persona_nombre' type="text" maxLength={100} autoFocus />
                                    <label className='active' htmlFor='persona_nombre'>Nombre</label>
                                </div>
                                <div className="input-field">
                                    <input onChange={e => l.cambiar({ certificado: e.target.value })} value={l.borrador.certificado} id='persona_certificado' type="text" maxLength={100} />
                                    <label className='active' htmlFor='persona_certificado'>{textos.certificado}</label>
                                </div>
                                <p style={estiloAyuda}>{textos.ayudaCertificado}</p>
                            </SeccionPanel>

                            <SeccionPanel id='foto' numero={2} titulo='Foto'>
                                <div className='panel_foto'>
                                    {l.borrador.imagen ? (
                                        <img src={l.borrador.imagen} alt='' />
                                    ) : (
                                        <span className='panel_foto_vacia'><i className='material-icons'>person</i></span>
                                    )}
                                    <p style={{ ...estiloAyuda, margin: "0" }}>
                                        {l.borrador.imagen ? 'Se muestra recortada en círculo.' : 'Aún no hay foto.'} Usa una imagen cuadrada.
                                    </p>
                                </div>
                                <div className="file-field input-field">
                                    <div className="btn" style={{backgroundColor:"#00496C"}}>
                                        <span>Archivo</span>
                                        <input onChange={elegirFoto} type="file" accept="image/*" />
                                    </div>
                                    <div className="file-path-wrapper">
                                        <input className="file-path" type="text" placeholder="Selecciona una imagen" value={foto?.name ?? ''} readOnly />
                                    </div>
                                </div>
                                {!agregando && (
                                    <p style={{ ...estiloAyuda, margin: "0 0 6px" }}>Al reemplazarla, la foto anterior se elimina.</p>
                                )}
                            </SeccionPanel>

                            {!agregando && (
                                <SeccionPanel id='uso' numero={3} titulo='Uso'>
                                    {enUso > 0 ? (
                                        <p className='panel_aviso'>{textos.enUso(cuantos(enUso))}</p>
                                    ) : (
                                        <p style={{ ...estiloAyuda, margin: "0 0 6px" }}>Ningún {usadoEn.singular} lo usa; se puede eliminar.</p>
                                    )}
                                </SeccionPanel>
                            )}
                        </form>
                    )}
                </PanelEdicion>
                <ListaAdmin
                    etiqueta={seccion}
                    encabezado={encabezado}
                    ayuda={textos.ayuda}
                    acciones={
                        <button type='button' className='boton_admin' onClick={abrirAgregar}>
                            + {textos.agregar}
                        </button>
                    }
                    busqueda={l.busqueda}
                    onBusqueda={l.setBusqueda}
                    placeholder={textos.buscar}
                    total={l.todos?.length ?? 0}
                    mostrados={l.lista?.length ?? 0}
                    cargando={l.cargando}
                    textoVacio={textos.vacio}
                    margen={l.panel.abierto ? l.panel.ancho : 0}
                >
                    {l.lista?.map(persona => {
                        const n = l.uso[persona._id] ?? 0;
                        return (
                            <FilaAdmin
                                key={persona._id}
                                activa={l.editar?._id === persona._id}
                                onClick={() => abrirEditar(persona)}
                                imagen={persona.imagen}
                                iniciales={iniciales(persona.nombre)}
                                titulo={persona.nombre}
                                subtitulo={persona.certificado}
                                insignias={<Insignia variante={n ? null : 'gris'}>{n ? 'En ' + cuantos(n) : 'Sin uso'}</Insignia>}
                            />
                        );
                    })}
                </ListaAdmin>
            </div>
        </Fragment>
    );
}

export default VistaPersonas;
